import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Stethoscope, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [rememberMe, setRememberMe] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password, rememberMe);
            toast.success('Welcome back!');
            const returnUrl = localStorage.getItem('medprep_return_url');
            localStorage.removeItem('medprep_return_url');
            navigate(returnUrl || '/dashboard');
        } catch (err) {
            const message = err.response?.data?.detail || 'Invalid email or password';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    <Stethoscope className="h-10 w-10 text-indigo-600" />
                    <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                        MedPrep NBME & CBSE
                    </span>
                </div>

                <Card className="border-slate-200 shadow-lg">
                    <CardHeader className="space-y-1">
                        <CardTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Manrope' }}>
                            Welcome Back
                        </CardTitle>
                        <CardDescription className="text-center">
                            Sign in to continue your practice
                        </CardDescription>
                    </CardHeader>
                    <form onSubmit={handleSubmit}>
                        <CardContent className="space-y-4">
                            {error && (
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm" data-testid="login-error">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    {error}
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password">Password</Label>
                                    <Link 
                                        to="/forgot-password" 
                                        className="text-sm text-indigo-600 hover:underline"
                                        data-testid="forgot-password-link"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    data-testid="login-password-input"
                                />
                            </div>

                            <div className="flex items-center space-x-2">
                                <Checkbox 
                                    id="remember-me" 
                                    checked={rememberMe}
                                    onCheckedChange={setRememberMe}
                                    data-testid="login-remember-me"
                                />
                                <Label 
                                    htmlFor="remember-me" 
                                    className="text-sm font-normal text-slate-600 cursor-pointer"
                                >
                                    Keep me logged in for 30 days
                                </Label>
                            </div>
                        </CardContent>
                        <CardFooter className="flex flex-col gap-4">
                            <Button 
                                type="submit" 
                                className="w-full" 
                                disabled={loading}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Signing in...
                                    </>
                                ) : (
                                    'Sign In'
                                )}
                            </Button>
                            <p className="text-sm text-slate-600 text-center">
                                Don't have an account?{' '}
                                <Link to="/register" className="text-indigo-600 hover:underline font-medium">
                                    Sign up
                                </Link>
                            </p>
                        </CardFooter>
                    </form>
                </Card>

                <p className="mt-6 text-center text-sm text-slate-500">
                    <Link to="/" className="hover:text-indigo-600">
                        ← Back to home
                    </Link>
                </p>
            </div>
        </div>
    );
}
